/**
 * Sentry init for the React frontend.
 *
 * Disabled cleanly if REACT_APP_SENTRY_DSN_FRONTEND is missing — no crash, no
 * console noise. PII scrubbing mirrors the backend (auth headers, cookies,
 * emails, Stripe keys, JWTs, MongoDB URIs) and 401/403/404 are filtered out.
 */
import * as Sentry from "@sentry/react";

const SENSITIVE_HEADER_KEYS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
  "x-stripe-signature",
]);

const SENSITIVE_KEY_RE = /password|pwd|secret|token|credential|api[._-]?key|jwt|refresh|access_token|cookie|authorization/i;

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const STRIPE_RE = /\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]{16,}\b/g;
const MONGO_RE = /mongodb(\+srv)?:\/\/\S+/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const CERT_PATH_RE = /\/[\w/.-]*(?:apple[\w.-]*|apns[\w.-]*|google[\w.-]*service[\w.-]*|wwdr[\w.-]*|\.p8|\.p12|\.pem|\.cer|\.key)/gi;

const PROTECTED_PATHS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
];

const IGNORED_STATUS = new Set([401, 403, 404, 422, 429]);

function scrubString(s) {
  if (typeof s !== "string" || !s) return s;
  return s
    .replace(EMAIL_RE, "[REDACTED_EMAIL]")
    .replace(STRIPE_RE, "[REDACTED_STRIPE]")
    .replace(MONGO_RE, "[REDACTED_MONGO_URI]")
    .replace(JWT_RE, "[REDACTED_JWT]")
    .replace(CERT_PATH_RE, "[REDACTED_CERT_PATH]");
}

function scrub(data) {
  if (data == null) return data;
  if (Array.isArray(data)) return data.map(scrub);
  if (typeof data === "string") return scrubString(data);
  if (typeof data === "object") {
    const out = {};
    for (const [k, v] of Object.entries(data)) {
      if (typeof k === "string" && SENSITIVE_KEY_RE.test(k)) {
        out[k] = "[REDACTED]";
      } else {
        out[k] = scrub(v);
      }
    }
    return out;
  }
  return data;
}

function beforeSend(event, hint) {
  // Drop normal HTTP states
  const status = event?.contexts?.response?.status_code;
  if (status && IGNORED_STATUS.has(status)) return null;

  // Strip request body & cookies for protected auth routes
  const url = event?.request?.url || "";
  if (PROTECTED_PATHS.some((p) => url.includes(p))) {
    if (event.request) {
      delete event.request.data;
      delete event.request.query_string;
    }
  }

  // Always strip cookies (httpOnly auth cookies leak tokens)
  if (event.request) {
    delete event.request.cookies;
  }

  // Scrub headers
  if (event?.request?.headers) {
    const cleaned = {};
    for (const [k, v] of Object.entries(event.request.headers)) {
      if (typeof k === "string" && SENSITIVE_HEADER_KEYS.has(k.toLowerCase())) {
        cleaned[k] = "[REDACTED]";
      } else {
        cleaned[k] = typeof v === "string" ? scrubString(v) : v;
      }
    }
    event.request.headers = cleaned;
  }

  // Scrub request body, breadcrumbs, exception messages, extras, tags
  if (event?.request?.data) event.request.data = scrub(event.request.data);
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((c) => ({
      ...c,
      message: typeof c.message === "string" ? scrubString(c.message) : c.message,
      data: c.data ? scrub(c.data) : c.data,
    }));
  }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((e) => ({
      ...e,
      value: typeof e.value === "string" ? scrubString(e.value) : e.value,
    }));
  }
  if (event.extra) event.extra = scrub(event.extra);
  if (event.tags) event.tags = scrub(event.tags);

  // User: keep id only (RGPD)
  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : {};
  }

  return event;
}

function beforeSendTransaction(event) {
  const url = event?.request?.url || "";
  if (PROTECTED_PATHS.some((p) => url.includes(p))) return null;

  if (event.request) {
    delete event.request.cookies;
    if (event.request.headers) {
      const cleaned = {};
      for (const [k, v] of Object.entries(event.request.headers)) {
        if (typeof k === "string" && SENSITIVE_HEADER_KEYS.has(k.toLowerCase())) {
          cleaned[k] = "[REDACTED]";
        } else {
          cleaned[k] = typeof v === "string" ? scrubString(v) : v;
        }
      }
      event.request.headers = cleaned;
    }
  }
  return event;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN_FRONTEND;
  if (!dsn) {
    // Graceful no-op
    return false;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || "development",
    release: process.env.REACT_APP_RELEASE || "loyalpass-frontend@1.0.0",
    sendDefaultPii: false,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1"),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend,
    beforeSendTransaction,
    integrations: [Sentry.browserTracingIntegration()],
  });
  return true;
}

// Helpers exported for tests
export const __scrub = scrub;
export const __scrubString = scrubString;
export const __beforeSend = beforeSend;
